import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../contexts/NotificationContext';
import { supabase } from '../../services/supabase';
import { fetchAiModels } from '../../services/dataService';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useData } from '../../contexts/DataContext';
import { Company } from '../../types/company.types';
import { DEFAULT_AI_MODEL } from '../../constants/aiModels';
import ArticleModelsSection from './ArticleModelsSection';
import AllowedProvidersSection from './AllowedProvidersSection';
import { isModelAllowed } from '../../utils/aiProviders';

interface DetailsTabProps {
  company: Company;
}

interface ModelOption {
  model: string;
  label: string | null;
}

const maskApiKey = (key: string) =>
  key.length <= 14 ? key : `${key.slice(0, 10)}${'•'.repeat(10)}${key.slice(-4)}`;

const DetailsTab = ({ company }: DetailsTabProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { hasAdminAccess, isSuperAdmin } = usePermissions();
  const { triggerCompanyFetch } = useData();
  const [isEditing, setIsEditing] = useState(false);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(false);
  const [formData, setFormData] = useState({
    name: company.name,
    openrouter_api_key: company.openrouter_api_key ?? '',
    ai_model: company.ai_model || DEFAULT_AI_MODEL,
  });

  useEffect(() => {
    setFormData({
      name: company.name,
      openrouter_api_key: company.openrouter_api_key ?? '',
      ai_model: company.ai_model || DEFAULT_AI_MODEL,
    });
  }, [company]);

  // Load the vetted model list (system-wide, managed by super admins) once.
  useEffect(() => {
    let active = true;
    (async () => {
      setModelsLoading(true);
      setModelsError(false);
      try {
        const data = await fetchAiModels();
        if (active) setModels(data.map((m) => ({ model: m.model, label: m.label })));
      } catch (error) {
        console.error('Error loading AI models:', error);
        if (active) setModelsError(true);
      } finally {
        if (active) setModelsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const canEdit = hasAdminAccess(company.id);
  const effectiveModel = company.ai_model || DEFAULT_AI_MODEL;
  const modelLabel = models.find((m) => m.model === effectiveModel)?.label || effectiveModel;
  const apiKeyConfigured = Boolean(company.openrouter_api_key);
  // Only the providers a super admin allowed for this company; the current
  // value stays selectable even if it is not in the vetted list any more.
  const allowedModels = models.filter((m) => isModelAllowed(m.model, company.allowed_ai_providers));
  const modelOptions: ModelOption[] = allowedModels.some((m) => m.model === formData.ai_model)
    ? allowedModels
    : [{ model: formData.ai_model, label: null }, ...allowedModels];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData({
      name: company.name,
      openrouter_api_key: company.openrouter_api_key ?? '',
      ai_model: company.ai_model || DEFAULT_AI_MODEL,
    });
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updateData = {
        name: formData.name,
        openrouter_api_key: formData.openrouter_api_key,
        ai_model: formData.ai_model,
      };

      const { error } = await supabase.from('companies').update(updateData).eq('id', company.id);
      if (error) {
        throw error;
      }

      // Re-fetch companies so the view reflects the saved values (the company comes from context).
      await triggerCompanyFetch();
      showNotification(t('company.company_updated_successfully'), 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating company:', error);
      showNotification(t('company.error_updating_company'), 'error');
    }
  };

  const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm ' +
    'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('company.company_details')}</h3>
          <p className="text-sm text-gray-500">{company.name}</p>
        </div>
        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
          >
            {t('common.edit')}
          </button>
        )}
        {isEditing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              {t('common.save_changes')}
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              {t('company.company_name')}
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label htmlFor="openrouter_api_key" className="block text-sm font-medium text-gray-700">
              {t('company.openrouter_api_key')}
            </label>
            <textarea
              id="openrouter_api_key"
              rows={3}
              name="openrouter_api_key"
              value={formData.openrouter_api_key}
              onChange={handleChange}
              className={`${inputClass} font-mono text-sm`}
              placeholder="sk-..."
            />
          </div>

          <div>
            <label htmlFor="ai_model" className="block text-sm font-medium text-gray-700">
              {t('company.ai_model')}
            </label>
            <select
              id="ai_model"
              name="ai_model"
              value={formData.ai_model}
              onChange={handleChange}
              className={inputClass}
              disabled={modelsLoading}
            >
              {modelOptions.map((option) => (
                <option key={option.model} value={option.model}>
                  {option.label || option.model}
                </option>
              ))}
            </select>
            {modelsLoading && (
              <p className="mt-1 text-xs text-gray-500">{t('company.ai_model_loading')}</p>
            )}
            {modelsError && (
              <p className="mt-1 text-xs text-red-600">{t('company.ai_model_load_error')}</p>
            )}
            {!modelsLoading && !modelsError && (
              <p className="mt-1 text-xs text-gray-500">{t('company.ai_model_hint')}</p>
            )}
          </div>
        </form>
      ) : (
        <dl className="divide-y divide-gray-100">
          <div className="flex flex-col gap-1 px-6 py-4 sm:flex-row sm:items-center sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 sm:w-52 sm:shrink-0">
              {t('company.company_name')}
            </dt>
            <dd className="text-sm text-gray-900">{company.name}</dd>
          </div>

          <div className="flex flex-col gap-1 px-6 py-4 sm:flex-row sm:items-center sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 sm:w-52 sm:shrink-0">
              {t('company.openrouter_api_key')}
            </dt>
            <dd className="flex min-w-0 items-center gap-2">
              {apiKeyConfigured ? (
                <>
                  <span className="truncate font-mono text-sm text-gray-900">
                    {maskApiKey(company.openrouter_api_key as string)}
                  </span>
                  <span className="inline-flex shrink-0 items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                    {t('company.api_key_configured')}
                  </span>
                </>
              ) : (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/20">
                  {t('company.api_key_not_set')}
                </span>
              )}
            </dd>
          </div>

          <div className="flex flex-col gap-1 px-6 py-4 sm:flex-row sm:items-center sm:gap-4">
            <dt className="text-sm font-medium text-gray-500 sm:w-52 sm:shrink-0">
              {t('company.ai_model')}
            </dt>
            <dd>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                {modelLabel}
              </span>
            </dd>
          </div>
        </dl>
      )}

      {/* Super admin: the providers this company may pick models from. */}
      {isSuperAdmin() && (
        <div className="border-t border-gray-100 px-6 py-5">
          <AllowedProvidersSection company={company} vettedModels={models.map((m) => m.model)} />
        </div>
      )}

      {/* Article generation: writer model and the critic slots, per company
          (moved here from the Critic Models page, 2026-09-10). */}
      <div className="border-t border-gray-100 px-6 py-5">
        <ArticleModelsSection
          companyId={company.id}
          canEdit={canEdit}
          companyKey={company.openrouter_api_key}
          allowedProviders={company.allowed_ai_providers}
        />
      </div>
    </div>
  );
};

export default DetailsTab;
